import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import { Plus, HardHat, Pencil, Trash } from "@phosphor-icons/react";
import { useCollectionSchema, useReferenceData } from "../hooks/useCollectionSchema";
import DynamicForm from "../components/DynamicForm";

const ProjectsPage = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [formData, setFormData] = useState({});

    // Get dynamic schema
    const { fields, displayName, loading: schemaLoading } = useCollectionSchema("projects");
    const { references } = useReferenceData(fields);

    useEffect(() => {
        fetchProjects();
    }, []);

    // Initialize form data when fields change
    useEffect(() => {
        if (fields.length > 0 && !editingProject) {
            initializeFormData();
        }
    }, [fields]);

    const initializeFormData = () => {
        const initial = {};
        fields.forEach(field => {
            initial[field.name] = field.default ?? (field.type === "number" ? 0 : field.type === "multi_reference" ? [] : "");
        });
        setFormData(initial);
    };

    const fetchProjects = async () => {
        try {
            const response = await axios.get(`${API}/projects`);
            setProjects(response.data);
        } catch (error) {
            toast.error("Error al cargar las obras");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        const missingRequired = fields
            .filter(f => f.required && (formData[f.name] === "" || formData[f.name] === undefined))
            .map(f => f.label || f.name);
        
        if (missingRequired.length > 0) {
            toast.error(`Campos requeridos: ${missingRequired.join(", ")}`);
            return;
        }

        try {
            if (editingProject) {
                await axios.put(`${API}/projects/${editingProject.id}`, formData);
                toast.success("Obra actualizada");
            } else {
                await axios.post(`${API}/projects`, formData);
                toast.success("Obra creada");
            }
            fetchProjects();
            setDialogOpen(false);
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al guardar");
        }
    };

    const handleEdit = (project) => {
        setEditingProject(project);
        const data = {};
        fields.forEach(field => {
            data[field.name] = project[field.name] ?? field.default ?? (field.type === "number" ? 0 : field.type === "multi_reference" ? [] : "");
        });
        setFormData(data);
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Eliminar esta obra?")) return;
        try {
            await axios.delete(`${API}/projects/${id}`);
            toast.success("Obra eliminada");
            fetchProjects();
        } catch (error) {
            toast.error("Error al eliminar");
        }
    };

    const resetForm = () => {
        setEditingProject(null);
        initializeFormData();
    };

    // Get display value for a field
    const getDisplayValue = (field, value) => {
        if (value === undefined || value === null || value === "") return "-";
        
        switch (field.type) {
            case "reference":
                const refData = references[field.ref_collection] || [];
                const refItem = refData.find(r => r.id === value);
                return refItem?.name || refItem?.title || value;
            case "multi_reference": {
                const opts = references[field.ref_collection] || [];
                const ids = Array.isArray(value) ? value : [];
                if (ids.length === 0) return "-";
                return ids.map(id => opts.find(o => o.id === id)?.name || id).join(", ");
            }
            case "boolean":
                return value ? "Sí" : "No";
            case "date":
                return new Date(value).toLocaleDateString("es-ES");
            case "number":
                if (field.name.includes("budget") || field.name.includes("price") || field.name.includes("presupuesto")) {
                    return `${value.toLocaleString("es-ES")} €`;
                }
                return value;
            case "select":
                return value.charAt(0).toUpperCase() + value.slice(1).replace("-", " ");
            default:
                return value;
        }
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const statusMap = {
            "activo": "badge-success",
            "pausado": "badge-warning",
            "completado": "badge-info"
        };
        const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : "-";
        return <span className={statusMap[status] || "badge-info"}>{label}</span>;
    };

    if (loading || schemaLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
            </div>
        );
    }

    // Get table columns from schema (exclude text, arrays and multi-reference — se ven en el formulario)
    const tableFields = fields.filter(f => f.type !== "text" && f.type !== "array" && f.type !== "multi_reference");
    const activeCount = projects.filter(p => p.status === "activo").length;

    return (
        <div className="space-y-6 animate-in" data-testid="projects-page">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl md:text-3xl">{displayName}</h1>
                    <p className="text-[#555555] mt-1">
                        {activeCount} de {projects.length} activas
                    </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button className="btn-primary" data-testid="add-project-btn">
                            <Plus size={20} className="mr-2" />
                            Nueva obra
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">
                                {editingProject ? "Editar obra" : "Nueva obra"}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="mt-4">
                            <DynamicForm 
                                fields={fields}
                                formData={formData}
                                setFormData={setFormData}
                                references={references}
                            />
                            <div className="flex gap-3 pt-6">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                                    Cancelar
                                </Button>
                                <Button type="submit" className="btn-primary flex-1" data-testid="project-save-btn">
                                    {editingProject ? "Guardar cambios" : "Crear"}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Projects Table */}
            <div className="bg-white border border-[#E5E5E5] rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="table-industrial">
                                {tableFields.map(field => (
                                    <TableHead key={field.name}>
                                        {field.label || field.name}
                                    </TableHead>
                                ))}
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {projects.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={tableFields.length + 1} className="text-center py-12">
                                        <HardHat size={48} className="mx-auto mb-4 text-[#CCCCCC]" />
                                        <p className="text-[#555555]">No hay obras registradas</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                projects.map((project) => (
                                    <TableRow key={project.id} className="table-industrial" data-testid={`project-row-${project.id}`}>
                                        {tableFields.map(field => (
                                            <TableCell key={field.name}>
                                                {field.name === "status" ? (
                                                    getStatusBadge(project[field.name])
                                                ) : (
                                                    getDisplayValue(field, project[field.name])
                                                )}
                                            </TableCell>
                                        ))}
                                        <TableCell>
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleEdit(project)}
                                                    className="p-2 hover:bg-[#F8F9FA] rounded-sm"
                                                    data-testid={`edit-project-${project.id}`}
                                                >
                                                    <Pencil size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(project.id)}
                                                    className="p-2 hover:bg-red-50 text-red-600 rounded-sm"
                                                    data-testid={`delete-project-${project.id}`}
                                                >
                                                    <Trash size={18} />
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default ProjectsPage;
